/**
 * Unlayer custom tool type slugs used in design JSON `contents[].type` fields.
 * Values must match editor tool registration (`custom#*` prefix) in Unlayer and
 * `public/unlayer-tools/*` — see ADR reusable-template-unlayer-schema.
 */
export const UNLAYER_INVOICE_ITEMS_TOOL_TYPE = 'custom#invoice_items';
export const UNLAYER_DUAL_SIGNATURE_TOOL_TYPE = 'custom#dual_signature_tool';
export const UNLAYER_CONTACT_FORM_TOOL_TYPE = 'custom#contact_form';
export const UNLAYER_PACKAGES_TOOL_TYPE = 'custom#packages_tool';
export const UNLAYER_QUESTIONNAIRE_TOOL_V2_TYPE = 'custom#questionnaire_element_v2';
export const UNLAYER_CANVA_DESIGN_TOOL_TYPE = 'custom#canva_design';

export const ESIGN_TOOL_TYPES = new Set([UNLAYER_DUAL_SIGNATURE_TOOL_TYPE]);

function extractCustomToolSlug(toolType: string): string {
  const match = toolType.match(/^custom#(.+)$/);
  return match ? match[1] : toolType;
}

export const ESIGN_TOOL_SLUGS = new Set([...ESIGN_TOOL_TYPES].map(extractCustomToolSlug));

/** All registered custom tool type slugs (editor + cloud export). */
export const ALL_UNLAYER_CUSTOM_TOOL_TYPES = [
  UNLAYER_INVOICE_ITEMS_TOOL_TYPE,
  UNLAYER_DUAL_SIGNATURE_TOOL_TYPE,
  UNLAYER_CONTACT_FORM_TOOL_TYPE,
  UNLAYER_PACKAGES_TOOL_TYPE,
  UNLAYER_QUESTIONNAIRE_TOOL_V2_TYPE,
  UNLAYER_CANVA_DESIGN_TOOL_TYPE,
] as const;

const UNLAYER_QUESTIONNAIRE_TOOL_V2_SLUG = extractCustomToolSlug(
  UNLAYER_QUESTIONNAIRE_TOOL_V2_TYPE
);

/**
 * True for a questionnaire v2 design block. Unlayer persists custom tools in two shapes:
 * `{ type: 'custom#questionnaire_element_v2' }` and `{ type: 'custom', slug: 'questionnaire_element_v2' }`
 * (blocks added after the first one), so matching `type` alone drops later tools.
 */
export function isQuestionnaireV2ToolBlock(block: { type?: unknown; slug?: unknown }): boolean {
  return (
    block.type === UNLAYER_QUESTIONNAIRE_TOOL_V2_TYPE ||
    (block.type === 'custom' && block.slug === UNLAYER_QUESTIONNAIRE_TOOL_V2_SLUG)
  );
}

/** True when `toolType` is an Unlayer custom tool slug (`custom#…`). */
export function isUnlayerCustomToolType(toolType: unknown): toolType is string {
  return typeof toolType === 'string' && toolType.startsWith('custom#');
}
